import type { ApiEndpointBuilder } from "../apiEndpointHelpers";
import type {
  AdminV2LinkedSupportConversationList,
  AdminV2LinkedSupportRecordType,
  AdminV2SupportConversation,
  AdminV2SupportConversationCreateInput,
  AdminV2SupportConversationDetail,
  AdminV2SupportConversationList,
  AdminV2SupportConversationListQuery,
  AdminV2SupportConversationRequester,
  AdminV2SupportConversationStatus,
} from "./adminV2SupportConversationTypes";

const BASE = "/admin/v2/support/conversations";
const LIST_TAG = { type: "AdminV2SupportConversation", id: "LIST" } as const;

export const buildAdminV2SupportConversationEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2SupportConversationRequesters: builder.query<
    { requesters: AdminV2SupportConversationRequester[] },
    { q: string }
  >({
    query: (params) => ({
      url: "/admin/v2/support/conversation-requesters",
      params,
      suppressAccessDeniedModal: true,
    }),
  }),
  createAdminV2SupportConversation: builder.mutation<
    { conversation: AdminV2SupportConversation },
    AdminV2SupportConversationCreateInput
  >({
    query: (data) => ({
      url: BASE,
      method: "POST",
      data,
      suppressAccessDeniedModal: true,
    }),
    invalidatesTags: (result) => (result ? [LIST_TAG] : []),
  }),
  getAdminV2SupportConversations: builder.query<
    AdminV2SupportConversationList,
    AdminV2SupportConversationListQuery
  >({
    query: (params) => ({ url: BASE, params }),
    providesTags: [LIST_TAG],
  }),
  getAdminV2SupportSearchCount: builder.query<
    { total: number },
    { q: string; readOwner: string }
  >({
    query: ({ q }) => ({
      url: `${BASE}/search-count`,
      params: { q },
      suppressAccessDeniedModal: true,
    }),
    providesTags: [LIST_TAG],
  }),
  // SUP-03: route back from an issue report or feature request to the
  // conversations that link it (support.view, scoped to Support orgs).
  getAdminV2LinkedSupportConversations: builder.query<
    AdminV2LinkedSupportConversationList,
    {
      recordType: AdminV2LinkedSupportRecordType;
      recordId: string;
      /** Client-side cache lifetime for an addressed, authority-bound read. */
      readOwner?: string;
      recovery?: boolean;
    }
  >({
    query: ({ recordType, recordId, recovery }) => ({
      url: `/admin/v2/support/${recordType}/${encodeURIComponent(recordId)}/conversations`,
      ...(recovery ? { params: { recovery: "1" } } : {}),
      // Linked Support reads already render access refusals inline.
      suppressAccessDeniedModal: true,
    }),
    providesTags: [LIST_TAG],
  }),
  getAdminV2SupportConversation: builder.query<
    AdminV2SupportConversationDetail,
    string | { conversationId: string; readOwner: string }
  >({
    query: (arg) => ({
      url: `${BASE}/${encodeURIComponent(typeof arg === "string" ? arg : arg.conversationId)}`,
    }),
    providesTags: (_result, _error, arg) => [
      {
        type: "AdminV2SupportConversation",
        id: typeof arg === "string" ? arg : arg.conversationId,
      },
    ],
  }),
  updateAdminV2SupportConversationStatus: builder.mutation<
    void,
    { conversationId: string; status: AdminV2SupportConversationStatus }
  >({
    query: ({ conversationId, ...data }) => ({
      url: `${BASE}/${encodeURIComponent(conversationId)}/status`,
      method: "PUT",
      data,
    }),
    invalidatesTags: (_result, _error, { conversationId }) => [
      LIST_TAG,
      { type: "AdminV2SupportConversation", id: conversationId },
    ],
  }),
  assignAdminV2SupportConversation: builder.mutation<
    void,
    { conversationId: string; assignedStaffUserId: string | null }
  >({
    query: ({ conversationId, ...data }) => ({
      url: `${BASE}/${encodeURIComponent(conversationId)}/owner`,
      method: "PUT",
      data,
    }),
    invalidatesTags: (_result, _error, { conversationId }) => [
      LIST_TAG,
      { type: "AdminV2SupportConversation", id: conversationId },
    ],
  }),
  addAdminV2SupportConversationNote: builder.mutation<
    void,
    { conversationId: string; bodyMarkdown: string }
  >({
    query: ({ conversationId, ...data }) => ({
      url: `${BASE}/${encodeURIComponent(conversationId)}/notes`,
      method: "POST",
      data,
    }),
    invalidatesTags: (_result, _error, { conversationId }) => [
      LIST_TAG,
      { type: "AdminV2SupportConversation", id: conversationId },
    ],
  }),
  replyToAdminV2SupportConversation: builder.mutation<
    {
      messageId: string;
      wasDuplicate: boolean;
      delivery: { status: "sent" | "failed" | "skipped"; message: string };
    },
    {
      conversationId: string;
      bodyMarkdown: string;
      expectedRequesterEmail: string;
      idempotencyKey: string;
      reviewed: true;
    }
  >({
    query: ({ conversationId, ...data }) => ({
      url: `${BASE}/${encodeURIComponent(conversationId)}/replies`,
      method: "POST",
      data,
    }),
    invalidatesTags: (_result, _error, { conversationId }) => [
      LIST_TAG,
      { type: "AdminV2SupportConversation", id: conversationId },
    ],
  }),
  escalateAdminV2SupportConversation: builder.mutation<
    {
      escalation: {
        id: string;
        kind: "issue" | "feature_request";
        title: string;
      };
      created: boolean;
    },
    {
      conversationId: string;
      kind: "issue" | "feature_request";
      existingRecordId?: string;
    }
  >({
    query: ({ conversationId, ...data }) => ({
      url: `${BASE}/${encodeURIComponent(conversationId)}/escalations`,
      method: "POST",
      data,
    }),
    invalidatesTags: (_result, _error, { conversationId }) => [
      LIST_TAG,
      { type: "AdminV2SupportConversation", id: conversationId },
    ],
  }),
});
